<template>
  <div class="manual">

    <header class="masthead">
      <div>
        <h1 class="title">Manual entries<span class="dot">.</span></h1>
        <p class="sub">
          Record an entry or an exit a door never saw: somebody forgot to badge
          out, or left the card at home. It counts in the log and the hours
          like a tap, and is marked as manual with your name on it.
        </p>
      </div>
    </header>

    <!-- Add or edit -->
    <div class="form">
      <label class="field grow">
        <span class="field-label">Person</span>
        <input
          v-model="form.personName"
          class="input"
          type="text"
          list="manual-people"
          placeholder="Start typing a name"
          :disabled="editing"
        >
        <datalist id="manual-people">
          <option v-for="p in people" :key="p.id" :value="p.name">{{ p.email }}</option>
        </datalist>
      </label>

      <label class="field">
        <span class="field-label">Office</span>
        <select v-model="form.office" class="input">
          <option value="">Select an office</option>
          <option v-for="o in offices" :key="o.key" :value="o.key">
            {{ o.name }} ({{ o.country }})
          </option>
        </select>
      </label>

      <label class="field">
        <span class="field-label">Direction</span>
        <select v-model="form.direction" class="input">
          <option value="in">In</option>
          <option value="out">Out</option>
        </select>
      </label>

      <label class="field">
        <span class="field-label">Date</span>
        <input v-model="form.date" class="input" type="date" :max="today">
      </label>

      <label class="field">
        <span class="field-label">Time <span class="optional">office local</span></span>
        <input v-model="form.time" class="input" type="time" step="60">
      </label>

      <label class="field grow">
        <span class="field-label">Reason <span class="optional">optional</span></span>
        <input v-model="form.note" class="input" type="text" maxlength="255" placeholder="Forgot card">
      </label>

      <button class="btn primary" :disabled="!canSave || saving" @click="save">
        {{ saving ? 'Saving…' : (editing ? 'Update' : 'Record') }}
      </button>

      <button v-if="editing" class="btn" @click="reset">Cancel</button>
    </div>

    <p v-if="formError" class="msg err">{{ formError }}</p>

    <p v-if="loading" class="msg">Reading entries</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="entries.length === 0" class="msg">
      No manual entries in the last {{ days }} days.
    </p>

    <table v-else class="table">
      <thead>
        <tr>
          <th>When</th>
          <th>Person</th>
          <th></th>
          <th>Office</th>
          <th>Reason</th>
          <th>Entered by</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="e in entries" :key="e.id">
          <td class="mono">{{ formatWhen(e.time) }}</td>
          <td>{{ nameOf(e) }}</td>
          <td>
            <span class="dir" :class="e.direction === 'ENTERED' ? 'dir-in' : 'dir-out'">
              {{ e.direction === 'ENTERED' ? 'In' : 'Out' }}
            </span>
          </td>
          <td>
            <span class="office">
              <span class="code">{{ e.country_code }}</span>
              {{ e.office_name }}
            </span>
          </td>
          <td class="note">{{ e.note || '—' }}</td>
          <td class="by">{{ e.entered_by }}</td>
          <td class="actions">
            <button class="link" @click="edit(e)">Edit</button>
            <button class="link danger" @click="remove(e)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="!loading && !error" class="foot">
      Door events cannot be edited or deleted here. Manual entries show as
      "manual" on Registrations and in the reports.
    </p>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import { getLocations } from '@/services/OfficeColourService'
import {
  getManualEntries, addManualEntry, updateManualEntry, deleteManualEntry
} from '@/services/ManualPresenceService'

const days = 30

const entries = ref([])
const people = ref([])
const offices = ref([])
const loading = ref(true)
const error = ref(null)
const saving = ref(false)
const formError = ref(null)
const editing = ref(null)   // the entry being edited, or null

const today = new Date().toISOString().slice(0, 10)

function blankForm() {
  const now = new Date()
  return {
    personName: '',
    office: '',
    direction: 'out',
    date: today,
    time: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    note: ''
  }
}

const form = ref(blankForm())

/** The person whose name is typed exactly, or null. Exact, so a half-typed
    name cannot record an entry against the wrong colleague. */
const person = computed(() => {
  const typed = form.value.personName.trim().toLowerCase()
  if (!typed) return null
  return people.value.find((p) => p.name.toLowerCase() === typed) ?? null
})

const canSave = computed(() =>
  (editing.value || person.value) && form.value.office && form.value.date && form.value.time
)

function nameOf(e) {
  return people.value.find((p) => p.id === e.entra_object_id)?.name
    || e.display_name
    || e.entra_object_id
}

function formatWhen(iso) {
  if (!iso) return ''
  // Already the office's local time; shown as sent, not re-zoned.
  return iso.slice(0, 16).replace('T', ' ')
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [list, locations, hierarchy] = await Promise.all([
      getManualEntries(days),
      getLocations(),
      getSiteHierarchy()
    ])

    entries.value = list
    offices.value = locations.map((l) => ({
      key: `${l.country}|${l.name}`,
      country: l.country,
      name: l.name
    }))

    // Everyone in every country: the person may be visiting another site.
    const all = []
    for (const country of hierarchy?.countries ?? []) {
      const inCountry = await getPeopleInCountry(country.code).catch(() => [])
      all.push(...(inCountry ?? []))
    }
    people.value = all.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

function reset() {
  editing.value = null
  form.value = blankForm()
  formError.value = null
}

function edit(e) {
  editing.value = e
  form.value = {
    personName: nameOf(e),
    office: `${e.country_code}|${e.office_name}`,
    direction: e.direction === 'ENTERED' ? 'in' : 'out',
    date: e.time.slice(0, 10),
    time: e.time.slice(11, 16),
    note: e.note ?? ''
  }
  formError.value = null
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function save() {
  if (!canSave.value) return

  const [countryCode, officeName] = form.value.office.split('|')
  const payload = {
    countryCode,
    officeName,
    direction: form.value.direction,
    time: `${form.value.date}T${form.value.time}`,
    note: form.value.note.trim()
  }

  saving.value = true
  formError.value = null

  try {
    if (editing.value) {
      await updateManualEntry({ id: editing.value.id, ...payload })
    } else {
      await addManualEntry({ entraObjectId: person.value.id, displayName: person.value.name, ...payload })
    }
    // Reload rather than patch in: the backend converts the time to the
    // office's zone and decides the ordering, so the list is its answer.
    entries.value = await getManualEntries(days)
    reset()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? 'Could not save'
  } finally {
    saving.value = false
  }
}

async function remove(e) {
  if (!window.confirm(`Delete the manual ${e.direction === 'ENTERED' ? 'entry' : 'exit'} for ${nameOf(e)} at ${formatWhen(e.time)}?`)) return

  try {
    await deleteManualEntry(e.id)
    entries.value = entries.value.filter((x) => x.id !== e.id)
    if (editing.value?.id === e.id) reset()
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  }
}

onMounted(load)
</script>


<style scoped>
.manual {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --paper: #ffffff;

  /* Left aligned, like every other page. Centring made the content drift right
     of the menu and the header logo, so nothing lined up with anything else. */
  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 26px;
}

.title {
  font-size: clamp(30px, 4.2vw, 44px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

/* ---------- Form ---------- */

.form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 22px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.grow { flex: 1; min-width: 180px; }

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.optional {
  font-weight: 400;
  letter-spacing: 0.02em;
  text-transform: none;
  color: #9aa5b2;
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  min-width: 220px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--paper);
  color: #191c1e;
  outline: none;
}

.input:focus { border-color: var(--ink); }
.input:disabled { background: var(--wash); color: var(--ink-soft); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:hover:not(:disabled) { background: var(--ink); color: var(--paper); }
.btn:disabled { opacity: 0.4; cursor: default; }

.btn.primary { background: var(--ink); color: var(--paper); }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 13px 18px;
  border-bottom: 1px solid var(--line);
  background: var(--wash);
}

.table td {
  padding: 13px 18px;
  border-bottom: 1px solid #f1f4f7;
}

.table tbody tr:last-child td { border-bottom: none; }

.mono {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 13.5px;
}

.office {
  display: inline-flex;
  align-items: center;
  gap: 9px;
}

.code {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: var(--paper);
  padding: 3px 7px;
  border-radius: 4px;
}

.orphan {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* Grey. This marks a door pointing at a location that no longer exists,
     which does need fixing, but the label already says so. */
  color: #64748b;
  background: #f1f4f7;
  padding: 3px 8px;
  border-radius: 4px;
}

.actions {
  text-align: right;
  white-space: nowrap;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  margin-left: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; }

@media (max-width: 760px) {
  .doors { padding: 0 20px 60px; }
  .form { flex-direction: column; align-items: stretch; }
  .input { min-width: 0; width: 100%; }
}

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 62ch;
}

.dir {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 5px;
}

.dir-in { background: #e3f3ec; color: #0f8b6c; }
.dir-out { background: #eef1f4; color: var(--ink-soft); }

.note, .by {
  color: var(--ink-soft);
  font-size: 14px;
}

.foot {
  font-size: 13px;
  color: var(--ink-soft);
  margin: 18px 0 0;
}
</style>
